import { FC, useEffect, useMemo, useState } from 'react';

import { DownloadIcon, PlusIcon, XIcon } from '@heroicons/react/outline';
import {
  CouponSettlementLineRow,
  GiftCouponSettlementDetailResponse,
  GiftCouponSettlementSummaryItem,
} from '@shared/api/couponSettlement/couponSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';

import {
  amountColorClass,
  amountLabel,
  couponCodeLabel,
  formatSettlementAmount,
  matchPendingRow,
} from '../../utils/coupon-settlement.utils';
import { formatDate } from '../../utils/delivery.utils';
import { statusColor, statusLabel } from '../../utils/rebate-settlement.utils';
import { getSettlementPayoutDate } from '../../utils/settlement.utils';
import { CouponSearchInput } from '../giftSettlement/CouponSearchInput';
import { PaginationFooter } from '../giftSettlement/PaginationFooter';
import { CouponSettlementLineTable } from './CouponSettlementLineTable';

/** 표시 페이지 크기 — 미정산 상세보기 모달과 동일 */
const PAGE_SIZE = 20;

/** 라인 구분 필터 — 전체 / 발송 수익금만 / 환불 차감만 (표시 필터일 뿐, 합계는 전체 기준) */
type LineKind = 'all' | 'send' | 'refund';

export interface CouponHistoryDetailModalProps {
  /** `:id/detail` 응답 — 요약 + 서버 정렬(발송→환불) 평탄화 행. null이면 로딩 중 */
  detail: GiftCouponSettlementDetailResponse | null;
  /**
   * detail 로딩 중 모달 골격(헤더)을 그릴 목록 행 요약 — 상세보기 클릭 즉시 모달을
   * 띄우고 본문만 스피너로 채우기 위한 값 (버튼 로딩 표시 대체).
   */
  fallbackSummary?: GiftCouponSettlementSummaryItem;
  onClose: () => void;
  /** 엑셀 다운로드 콜백(미지정 시 버튼 숨김) */
  onExport?: (d: GiftCouponSettlementDetailResponse) => void;
  /**
   * 라인 편집 모드 (Admin·Draft 한정) — 행별 "제외"와 "쿠폰 추가" 버튼 노출.
   * 실제 PATCH·재조회는 호출부(CouponHistorySection) 몫이고 여기선 표시만.
   */
  editable?: boolean;
  /** 행 제외 요청 — editable일 때만 호출됨 */
  onRemoveRow?: (row: CouponSettlementLineRow) => void;
  /** 제외 처리 중인 쿠폰 id (해당 행 버튼 로딩 표시) */
  removingCouponId?: string | null;
  /** "쿠폰 추가" 버튼 클릭 — 미정산 쿠폰 피커 열기 */
  onAddCoupons?: () => void;
  /** 재발행(isAdditional) 뱃지 표시 — 현재 모든 view에서 켬 (2026-07-02 view 동일 결정) */
  showAdditionalBadge?: boolean;
  /**
   * 집행 당시 초과 제외 행(excludedRows) 섹션 표시 (가이드 §6-1).
   * 지급 안 된 참고 기록이라 합계·검색·페이지네이션과 무관한 별도 섹션으로만 그린다.
   */
  showExcludedRows?: boolean;
  /**
   * 초과 제외 행을 정산에 강제 포함 (editable일 때만 버튼 노출 — 스펙 §5-2b).
   * PATCH allowOverQuotaCouponIds로 쿼터 가드를 면제하고 정식 라인으로 편입한다.
   */
  onIncludeExcludedRow?: (row: CouponSettlementLineRow) => void;
  /** 강제 포함 처리 중인 쿠폰 id (해당 행 버튼 로딩 표시) */
  includingCouponId?: string | null;
}

/**
 * 쿠폰 정산서 상세 모달. 목록(summary)에서 상세보기를 눌렀을 때만
 * detail API로 불러온 데이터를 받는다 — 발송/환불 합계도 서버 집계값 그대로.
 */
export const CouponHistoryDetailModal: FC<CouponHistoryDetailModalProps> = ({
  detail,
  fallbackSummary,
  onClose,
  onExport,
  editable = false,
  onRemoveRow,
  removingCouponId,
  onAddCoupons,
  showAdditionalBadge = false,
  showExcludedRows = false,
  onIncludeExcludedRow,
  includingCouponId,
}) => {
  // detail이 오기 전엔 목록 행 요약으로 헤더만 그린다 (본문은 스피너)
  const s = detail?.settlement ?? fallbackSummary;
  const rows = useMemo(() => detail?.rows ?? [], [detail]);
  // 집행 당시 초과 제외분 — 표기·감사용 스냅샷 (합계 미포함). 초과건은 구매금액
  // 0원(수수료 0원) 건이라 "지급 보류" 금액이 아니고, 실제로 나간 도매가(발송 실비)를
  // 낙전 정산에서 상조에 청구(회수)한다 — 섹션 합계도 도매가 기준.
  const excluded = showExcludedRows ? detail?.excludedRows ?? [] : [];
  const excludedWholeSum = excluded.reduce((sum, r) => sum + (r.priceWhole ?? 0), 0);

  // 통합 검색 (미정산 상세보기와 동일 매칭) — 표시 필터 전용, 합계는 전체 기준
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  // 구분 탭 — 발송 수익금/환불 차감을 따로 볼 수 있게 (기본 전체 = 서버 정렬 그대로)
  const [kind, setKind] = useState<LineKind>('all');
  const sendRowCount = useMemo(() => rows.filter((r) => !r.isRefund).length, [rows]);
  const refundRowCount = rows.length - sendRowCount;
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (kind === 'all' || (kind === 'refund') === r.isRefund) &&
          matchPendingRow(r, query),
      ),
    [rows, query, kind],
  );
  useEffect(() => {
    // 검색어·구분이 바뀌면 페이지를 처음으로
    setPage(1);
  }, [query, kind]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage],
  );
  const searching = query.trim().length > 0;

  // detail도 fallbackSummary도 없으면 그릴 게 없다 (호출부 가드의 최후 방어)
  if (!s) return null;

  const payoutDate = s.executedAt
    ? new Date(s.executedAt)
    : getSettlementPayoutDate(s.year, s.month);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative my-4 flex max-h-[92vh] w-full max-w-[97vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
              {s.year}년 {s.month}월 쿠폰 정산 상세
              <span
                className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${statusColor(
                  s.status,
                )}`}
              >
                {statusLabel(s.status)} · {formatDate(payoutDate)}
                {s.status !== GiftSettlementStatus.Paid && ' (예정)'}
              </span>
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              발송 수익금(+)과 환불 차감(−)이 함께 포함됩니다.
              {editable &&
                ' 제외한 쿠폰은 미정산으로 돌아가고, 정산금은 서버가 재계산합니다.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {detail && editable && onAddCoupons && (
              <button
                onClick={onAddCoupons}
                className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
              >
                <PlusIcon className="h-4 w-4" />
                쿠폰 추가
              </button>
            )}
            {detail && onExport && (
              <button
                onClick={() => onExport(detail)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <DownloadIcon className="h-4 w-4" />
                엑셀 다운로드
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="닫기"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* detail 로딩 중 — 헤더만 그리고 본문은 스피너 (버튼 로딩 대체) */}
        {!detail && (
          <div className="center-box py-24">
            <Loading />
          </div>
        )}

        {detail && (
          <>
            {/* 툴바 — 구분 탭(전체/발송/환불) + 통합 검색 (미정산 상세보기와 동일 매칭 규칙) */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setKind('all')}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                    kind === 'all'
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  전체 · {rows.length}건
                </button>
                <button
                  onClick={() => setKind('send')}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                    kind === 'send'
                      ? 'border-emerald-600 bg-emerald-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  발송 수익금 · {sendRowCount}건
                </button>
                <button
                  onClick={() => setKind('refund')}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                    kind === 'refund'
                      ? 'border-rose-600 bg-rose-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  환불 차감 · {refundRowCount}건
                </button>
                {searching && (
                  <span className="text-xs text-gray-400">
                    검색 결과 {filtered.length}건 — 합계는 전체 기준
                  </span>
                )}
              </div>
              <CouponSearchInput value={query} onChange={setQuery} />
            </div>

            <div className="overflow-auto px-5 py-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg bg-gray-50 px-5 py-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  <span>
                    발송 수익금{' '}
                    <span className="font-semibold text-emerald-700">
                      +{s.sendAmount.toLocaleString('ko-KR')}
                    </span>
                  </span>
                  <span className="text-gray-300">−</span>
                  <span>
                    환불 차감{' '}
                    <span className="font-semibold text-rose-700">
                      {s.refundAmount.toLocaleString('ko-KR')}
                    </span>
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-gray-500">
                    {amountLabel(s.totalAmount)}
                  </span>
                  <span
                    className={`text-3xl font-bold ${amountColorClass(s.totalAmount)}`}
                  >
                    {formatSettlementAmount(s.totalAmount)}
                  </span>
                </div>
              </div>

              {filtered.length === 0 && rows.length > 0 ? (
                <div className="rounded-lg bg-white p-8 text-center text-gray-400 shadow">
                  {searching ? '검색 결과가 없습니다.' : '해당 구분의 라인이 없습니다.'}
                </div>
              ) : (
                <CouponSettlementLineTable
                  rows={paged}
                  startIndex={(safePage - 1) * PAGE_SIZE}
                  totalCount={rows.length}
                  totalAmount={s.totalAmount}
                  totalFee={s.totalFee}
                  feeRate={s.feeRate}
                  method={s.method}
                  onRemoveRow={editable ? onRemoveRow : undefined}
                  removingCouponId={removingCouponId}
                  showAdditionalBadge={showAdditionalBadge}
                />
              )}

              {/* 페이지네이션 (표시용 — 검색 결과 기준) */}
              <PaginationFooter page={safePage} pageCount={pageCount} onPage={setPage} />

              {/* 집행 당시 초과 제외분 (가이드 §6-1) — 구매금액 0원 건의 참고 기록.
              지급 정산 합계에 섞지 않고 라인 표와 분리된 앰버 섹션으로만 그린다. */}
              {excluded.length > 0 && (
                <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
                  <p className="mb-3 text-sm font-semibold text-amber-800">
                    구매수량 초과 발송 {excluded.length}건 — 구매금액 0원(수수료 0원)
                    건입니다. 도매가(발송 실비) {excludedWholeSum.toLocaleString('ko-KR')}
                    원은 낙전 정산 시 상조 상계 예정입니다
                    {editable && onIncludeExcludedRow && (
                      <span className="ml-1 font-normal text-amber-600">
                        · 포함 버튼으로 개별 건을 지급 대상에 강제 편입할 수 있습니다
                      </span>
                    )}
                  </p>
                  <div className="max-h-64 overflow-auto rounded-md bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-center">순번</th>
                          <th className="px-3 py-2 text-left">쿠폰번호</th>
                          <th className="px-3 py-2 text-left">구매상품명</th>
                          <th className="px-3 py-2 text-left">구매자</th>
                          <th className="px-3 py-2 text-left">발송일</th>
                          <th className="px-3 py-2 text-right">구매금액</th>
                          <th className="px-3 py-2 text-right">수수료</th>
                          <th className="px-3 py-2 text-right">정산금(도매가 회수)</th>
                          {editable && onIncludeExcludedRow && (
                            <th className="px-3 py-2 text-center">관리</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {excluded.map((r, idx) => (
                          <tr key={r.giftCouponId}>
                            <td className="px-3 py-2.5 text-center text-xs tabular-nums text-gray-500">
                              {idx + 1}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-700">
                              {couponCodeLabel(r.fullCode)}
                              {r.isAdditional && (
                                <span className="ml-1.5 inline-block whitespace-nowrap rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium text-sky-700">
                                  재발행
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-600">
                              {r.purchaseItemName || '-'}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-600">
                              {r.buyerName || '-'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                              {formatDate(r.deliveredAt)}
                            </td>
                            {/* 초과건 = 대응 구매 없음 → 구매금액 0원·수수료 0원,
                                정산금은 −도매가(발송 실비 회수 — 낙전 정산에서 처리) */}
                            <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs tabular-nums text-gray-500">
                              0
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs tabular-nums text-gray-500">
                              0
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs font-medium tabular-nums text-rose-700">
                              {r.priceWhole != null
                                ? `−${r.priceWhole.toLocaleString('ko-KR')}`
                                : '-'}
                            </td>
                            {editable && onIncludeExcludedRow && (
                              <td className="whitespace-nowrap px-3 py-2.5 text-center">
                                <button
                                  onClick={() => onIncludeExcludedRow(r)}
                                  disabled={!!includingCouponId}
                                  title="쿼터 가드를 면제하고 이 발송을 지급 대상 라인으로 편입합니다"
                                  className="rounded border border-amber-400 bg-white px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  {includingCouponId === r.giftCouponId
                                    ? '포함 중...'
                                    : '정산에 포함'}
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
